import { EditorState, TextSelection, Transaction } from 'prosemirror-state'
import { Mapping } from 'prosemirror-transform'

import { KeyhandlerMetadataKey } from 'common/Prose/constants'
import { KeyhandlerMetadata } from 'common/Prose/types'
import { getPreviousTextPosition } from 'common/Prose/utils'
import { CellSelection } from 'common/Prose/utils/table/CellSelection'
import { MENTION_PROMPT_PLUGIN_KEY } from 'proseShared/plugins/MentionPrompt/MentionPrompt'
import { PredictAIPluginKey } from 'proseShared/plugins/PredictAI/PredictAI'

import { ALMANAC_DROP_CURSOR_PLUGIN_KEY } from '../AlmanacDropCursor/constants'
import { FloatingMenuAnchorPluginKey } from '../FloatingMenuAnchorPlugin/FloatingMenuAnchorPlugin'
import { specialCaseHandling } from './commands'
import { getTrackChangesState, trackChangesKey } from './constants'
import { stepTracker } from './stepTracker'
import { getContext, getPreservedSelection, isDeletion } from './utils'

/**
 * Track a transaction. If the transaction modifies the document and is
 * originated locally by the current user, we want to go through all the steps
 * and apply a version of these steps that include track changes marks and
 * attributes on the changed content.
 *
 * @param step - the step to track
 * @param state - the state of the editor
 * @param tr - the wrapped transaction. NOTE: This is not the original transaction
 * @param map - the mapping accumulated during the wrapping
 * @param index - the index of the step in the transaction being wrapped
 */
export function trackTransaction(tr: Transaction, state: EditorState, userId: number) {
  const handlerMetadata: KeyhandlerMetadata = tr.getMeta(KeyhandlerMetadataKey)
  // DEV: Found myself adding this multiple times in the exercise of fixing tracked changes
  // if (tr.steps.length)
  //   console.log(`trackTransaction`, JSON.stringify([tr.steps.map((step) => step.toJSON())], null, 2), handlerMetadata)

  const wrappedTr = state.tr
  const map = new Mapping()

  try {
    if (handlerMetadata && specialCaseHandling(handlerMetadata, { originalTr: tr, wrappedTr, map, state, userId })) {
      console.debug(`Tracked change handled by special handler, command metadata:`, handlerMetadata) // eslint-disable-line no-console
    } else if (!shouldTrack(tr)) {
      console.debug(`Tracked change ignored, command metadata:`, handlerMetadata) // eslint-disable-line no-console
      return tr
    } else {
      stepTracker({ originalTr: tr, wrappedTr, map, state, userId })
    }

    // In upcoming PR allow removing columns/rows and then fix trackSelection to handle CellSelections
    if (!(tr.selection instanceof CellSelection)) {
      trackSelection(tr, wrappedTr, map, state)
    }

    cloneMeta(tr, wrappedTr)

    return wrappedTr
  } catch (error) {
    console.error(error) // eslint-disable-line no-console
    return tr
  }
}

/**
 * Whether a transaction should be tracked or not. This assumes that track
 * changes is on, and only inspects the current transaction.
 *
 * @param tr - the inspected transaction
 */
function shouldTrack(tr: Transaction) {
  const context = getContext(tr)

  if (
    // The transaction did not originate locally
    tr.getMeta('source') === 'remote' ||
    // This is an undo / redo operation
    tr.getMeta('history$') ||
    // A tracked changes is being accepted or rejected
    tr.getMeta(trackChangesKey)?.disposition ||
    // The transaction has no steps
    tr.steps.length === 0 ||
    // Context explictly says to ignore
    context?.ignore
  ) {
    return false
  }

  return true
}

/**
 * Set the correct selection for a tracked transaction and adjust the cursor
 * position.
 *
 * @param originalTr - the transaction being intercepted
 * @param tr - the transaction being tracked
 * @param map - the mapping of the tracked transaction
 * @param state - the state of the editor
 */
function trackSelection(originalTr: Transaction, tr: Transaction, map: Mapping, state: EditorState) {
  const preservedSelection = getPreservedSelection(originalTr)
  if (preservedSelection && preservedSelection instanceof TextSelection) {
    const $anchor = tr.doc.resolve(preservedSelection.$anchor.pos)
    const $head = tr.doc.resolve(preservedSelection.$head.pos)
    tr.setSelection(new TextSelection($anchor, $head))
    return
  }
  /**
   * TODO:
   * - check gap cursors
   */
  const trackChangesState = getTrackChangesState(state)
  if (trackChangesState.store.hideDecorations && tr.selection.empty && tr.selection.from < tr.selection.to) {
    let $candidatePos = tr.selection.$from
    while (isDeletion($candidatePos.node()) && $candidatePos.pos > 1) {
      $candidatePos = getPreviousTextPosition($candidatePos)
    }
    tr.setSelection(new TextSelection($candidatePos))
    return
  }
  try {
    /**
     * Since deletions are tracked as marks and the content is kept, we have to
     * manually set the cursor position to the place it would appear if the
     * content was indeed removed, right before the start of the deletion.
     */
    if (originalTr.selection instanceof TextSelection && originalTr.selection.from < state.selection.from) {
      const caretPos = map.map(originalTr.selection.from, -1)
      tr.setSelection(new TextSelection(tr.doc.resolve(caretPos)))
    } else {
      tr.setSelection(originalTr.selection.map(tr.doc, map))
    }
  } catch (error) {
    console.debug(error) // eslint-disable-line no-console
  }
}

/**
 * Clone the meta from a transaction to another transaction. This is necessary
 * to avoid hijacking plugins that rely on meta to work, for example the slash
 * menu.
 *
 * @param fromTr - the transaction from which the meta will be copied
 * @param toTr - the transaction to which the meta will be copied
 */
function cloneMeta(fromTr: Transaction, toTr: Transaction) {
  ;[FloatingMenuAnchorPluginKey, MENTION_PROMPT_PLUGIN_KEY, ALMANAC_DROP_CURSOR_PLUGIN_KEY, PredictAIPluginKey].forEach(
    (key) => {
      toTr.setMeta(key, fromTr.getMeta(key))
    },
  )
}
